/**
 * Operator Dark — dark-first premium design language for the whole product.
 * Pairs with Clean Enterprise (light). Console aesthetic: near-black canvas,
 * hairline borders, blue accent, mono for numbers/labels.
 * Spec: docs/superpowers/specs/2026-07-14-operator-design-language-design.md
 */
import { createTheme, Theme } from "@mui/material/styles";
import type { ThemeConfig } from "./types";
import { MONO_FONT } from "@/shared/typography";
import { buildOperatorMuiComponents } from "@/shared/operator";

export const operatorDarkTokens = {
  bg: "#0A0B0F",
  surface: "#101218",
  surface2: "#0C0E13",
  ink: "rgba(255,255,255,0.94)",
  ink2: "#94A3B8",
  ink3: "#64748B",
  accent: "#2563EB",
  accentText: "#60A5FA",
  accentLight: "rgba(37,99,235,0.16)",
  accentDark: "#1E40AF",
  success: "#4ADE80",
  successLight: "rgba(34,197,94,0.14)",
  warning: "#FBBF24",
  warningLight: "rgba(251,191,36,0.13)",
  error: "#F87171",
  errorLight: "rgba(220,38,38,0.12)",
  info: "#60A5FA",
  infoLight: "rgba(37,99,235,0.14)",
  border: "rgba(255,255,255,0.09)",
  sans: '"Instrument Sans", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  mono: MONO_FONT,
} as const;

export const createOperatorDarkTheme = (): Theme => {
  const t = operatorDarkTokens;
  return createTheme({
    palette: {
      mode: "dark",
      primary: { main: t.accent, light: t.accentText, dark: t.accentDark },
      background: { default: t.bg, paper: t.surface },
      text: { primary: t.ink, secondary: t.ink2, disabled: t.ink3 },
      success: { main: t.success },
      warning: { main: t.warning },
      error: { main: t.error },
      info: { main: t.info },
      divider: t.border,
    },
    typography: {
      fontFamily: t.sans,
      fontSize: 13,
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
      button: { textTransform: "none", fontWeight: 500 },
      overline: {
        fontFamily: t.mono,
        fontSize: 10,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
      },
    },
    shape: { borderRadius: 8 },
    components: buildOperatorMuiComponents(t),
  });
};

export const operatorDarkThemeConfig: ThemeConfig = {
  id: "operator-dark",
  name: "Operator Dark",
  description: "Dark-first console aesthetic — near-black canvas, hairline borders, blue accent, mono numbers",
  createTheme: createOperatorDarkTheme,
};
